import React, { useEffect, useState } from "react";
import { Grid } from "@material-ui/core";

import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import withAuthority from "../../../../components/Auth/withAuthority";
import DashboardCard from "./DashboardCard";
import { useRouteMatch } from "react-router-dom";
import { saveLogs } from "../../../../utils/firebase/logs";

export interface DashboardDetailsProps {
  dashboardNodeList: any;
  handleUpdateDashboardInfo: any;
}

/**
 * DashboardDetails Component:
 *
 * This component represents the detailed view of the dashboard, displaying individual cards
 * for different settings along with their status and providing functionality to toggle
 * these settings.
 */
const DashboardDetails: React.SFC<DashboardDetailsProps> = ({
  dashboardNodeList,
  handleUpdateDashboardInfo,
}) => {
  const [isOrderingActive, setIsOrderingActive] = useState(false);
  const [isPreOrderActive, setIsPreOrderActive] = useState(false);
  const [isCarrierBagChargeActive, setIsCarrierBagChargeActive] =
    useState(false);
  const [isOrderingStatusActive, setIsOrderingStatusActive] = useState(false);
  const [isOrderCommentsEnabled, setIsOrderCommentsEnabled] = useState(false);
  const [isPreOrderingStatusActive, setIsPreOrderingStatusActive] =
    useState(false);
  const [
    isCarrierBagChargingStatusActive,
    setIsCarrierBagChargingStatusActive,
  ] = useState(false);
  const [nodeList, setNodeList] = useState<any>();
  const [error, setError] = useState("");

  const match: any = useRouteMatch();

  useEffect(() => {
    const { isOrderingActive, isPreOrderActive, carrierBagCharge, isOrderCommentsEnabled } =
      dashboardNodeList;
    setIsOrderingActive(isOrderingActive);
    setIsOrderingStatusActive(isOrderingActive);
    setIsOrderCommentsEnabled(isOrderCommentsEnabled)
    setIsPreOrderActive(isPreOrderActive);
    setIsPreOrderingStatusActive(isPreOrderActive);

    // CarrierBagCharge value If 0.00, changes the status of 'true'.
    if (carrierBagCharge !== "0.00") {
      setIsCarrierBagChargeActive(true);
      setIsCarrierBagChargingStatusActive(true);
    } else {
      // Otherwise, changes the status of 'false'.
      setIsCarrierBagChargeActive(false);
      setIsCarrierBagChargingStatusActive(false);
    }
    setNodeList(dashboardNodeList);
  }, [dashboardNodeList]);

  // Change Ordering Switch status.
  const handleOrderingSwitchActivate = (e: any) => {
    const { checked } = e.target;
    setIsOrderingActive(checked);
    nodeList.isOrderingActive = checked;
    const message = "Eatpresto Online Ordering Changed";
    const summary = [`Online Ordering changed from ${!checked ? "Activate" :"Deactivate"} to ${checked ? "Activate" :"Deactivate"}`];

    const body = {
      message,
      summary,
      type: "dashboard",
      code: "update",
      access_type: true,
      metadata: { changedBy: "" },
    };
    handleUpdateDashboardInfo(nodeList, body);
  };

    const handleCommentingSwitchActivate = (e: any) => {
    const { checked } = e.target;
    setIsOrderCommentsEnabled(checked);
    nodeList.isOrderCommentsEnabled = checked;
    const message = "Eatpresto Online Ordering Changed";
    const summary = [`Online Ordering Comments changed from ${!checked ? "Activate" :"Deactivate"} to ${checked ? "Activate" :"Deactivate"}`];

    const body = {
      message,
      summary,
      type: "dashboard",
      code: "update",
      access_type: true,
      metadata: { changedBy: "" },
    };
    handleUpdateDashboardInfo(nodeList, body);
  };

  // Change Pre Ordering Switch status.
  const handlePreOrderSwitchActivate = (e: any) => {
    const { checked } = e.target;
    setIsPreOrderActive(checked);
    nodeList.isPreOrderActive = checked;

    const message = "Scheduled Ordering Changed";
    const summary = [
      `Scheduled Ordering changed from ${!checked ? "Activate" :"Deactivate"} to ${checked ? "Activate" :"Deactivate"}`,
    ];
    const body = {
      message,
      summary,
      type: "dashboard",
      code: "update",
      access_type: true,
      metadata: { changedBy: "" },
    };
    handleUpdateDashboardInfo(nodeList, body);
  };

  // Change Carrier Bag Charge Switch status.
  const handleCarrierBagChargeActivate = (e: any) => {
    const { checked } = e.target;
    setIsCarrierBagChargeActive(checked);
    // CarrierBagCharge If 'true', changes the value of carrierBagCharge to 0.10'.
    if (checked) {
      nodeList.carrierBagCharge = "0.10";
    } else {
      // CarrierBagCharge If 'false', changes the value of carrierBagCharge to '0.00'.
      nodeList.carrierBagCharge = "0.00";
    }

    const message = "Carrier Bag Charge Changed";
    const summary = [
      `Carrier Bag Charge changed from ${checked ? "0.00" : "0.10"} to ${
        nodeList.carrierBagCharge
      }`,
    ];
    const body = {
      message,
      summary,
      type: "dashboard",
      code: "update",
      access_type: true,
      metadata: { changedBy: "" },
    };
    handleUpdateDashboardInfo(nodeList, body);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            backgroundColor={isOrderingStatusActive}
            title={"Online Ordering"}
            subTitle={
              isOrderingStatusActive
                ? "You are accepting orders."
                : "You are not accepting orders."
            }
            handleOrderingSwitchActivate={handleOrderingSwitchActivate}
            isActive={isOrderingActive}
            activeButtonLabel="Online Ordering"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            backgroundColor={isPreOrderingStatusActive}
            title="Scheduled Ordering"
            subTitle={
              isPreOrderingStatusActive
                ? "Customers can pre-order for a later date and time."
                : "Customers can only order for now."
            }
            handleOrderingSwitchActivate={handlePreOrderSwitchActivate}
            isActive={isPreOrderActive}
            activeButtonLabel="Scheduled Ordering"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            backgroundColor={isCarrierBagChargingStatusActive}
            title="Carrier Bag Charge"
            subTitle={
              isCarrierBagChargingStatusActive
                ? "You are charging 10p per order for carrier bags"
                : "You are not charging for carrier bags"
            }
            handleOrderingSwitchActivate={handleCarrierBagChargeActivate}
            isActive={isCarrierBagChargeActive}
            activeButtonLabel="Carrier Bag Charge"
          />
        </Grid>
          <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            backgroundColor={isOrderCommentsEnabled}
            title={"Commenting"}
            subTitle={
              isOrderCommentsEnabled
                ? "You are accepting comments."
                : "You are not accepting comments."
            }
            handleOrderingSwitchActivate={handleCommentingSwitchActivate}
            isActive={isOrderCommentsEnabled}
            activeButtonLabel="Commenting"
          />
        </Grid>
      </Grid>
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(DashboardDetails, Authorities.DASHBOARD_READ);
